import React from "react";
import type { Locale } from "../i18n";
import { usePageSsrData } from "./data-context";

export type SsrDataStore = Record<string, unknown>;

export interface SsrPageContext {
	url: string;
	pathname: string;
	locale: Locale;
	headers: Record<string, string | undefined>;
	cookie?: string;
}

export interface SsrPageComponentProps<TData> {
	ssrData: TData;
}

export interface SsrPageMetadataImage {
	url: string;
	alt?: string;
}

export interface SsrPageMetadata {
	title?: string;
	description?: string;
	keywords?: string[];
	canonical?: string;
	robots?: string;
	language?: string;
	alternates?: Record<string, string>;
	openGraph?: {
		type?: string;
		title?: string;
		description?: string;
		url?: string;
		siteName?: string;
		locale?: string;
		images?: SsrPageMetadataImage[];
		publishedTime?: string;
		modifiedTime?: string;
	};
	twitter?: {
		card?: string;
		title?: string;
		description?: string;
		images?: string[];
	};
	jsonLd?: Record<string, unknown>[];
}

export type SsrPageComponent<TData> = React.ComponentType<
	SsrPageComponentProps<TData>
>;

export interface SsrPageStatic<TData = unknown> {
	readonly __ssrPage: true;
	readonly key: string;
	readonly getSsrData?: (ctx: SsrPageContext) => Promise<TData> | TData;
	readonly getSsrMetadata?: (
		ctx: SsrPageContext,
		data: TData,
	) => Promise<SsrPageMetadata | undefined> | SsrPageMetadata | undefined;
}

/** createSsrPage 返回的就是可直接放进 routes 的 React 组件。 */
export type SsrPage<TData = unknown> = React.ComponentType &
	SsrPageStatic<TData>;

/** @deprecated 旧名，指向 SsrPage */
export type SsrPageConfig<TData = unknown> = SsrPage<TData>;

export const createSsrPage = <TData,>(config: {
	key: string;
	component: SsrPageComponent<TData>;
	getSsrData?: (ctx: SsrPageContext) => Promise<TData> | TData;
	getSsrMetadata?: (
		ctx: SsrPageContext,
		data: TData,
	) => Promise<SsrPageMetadata | undefined> | SsrPageMetadata | undefined;
}): SsrPage<TData> => {
	const Page = Object.assign(
		() => {
			const ssrData = usePageSsrData<TData>(config.key);
			const Component = config.component;
			return <Component ssrData={ssrData as TData} />;
		},
		{
			displayName: `SsrPage(${config.key})`,
			__ssrPage: true as const,
			key: config.key,
			getSsrData: config.getSsrData,
			getSsrMetadata: config.getSsrMetadata,
		},
	);

	return Page as SsrPage<TData>;
};

export const isSsrPage = (value: unknown): value is SsrPage => {
	if (typeof value !== "function") {
		return false;
	}

	return (value as unknown as SsrPageStatic).__ssrPage === true;
};

/** @deprecated 旧名，指向 isSsrPage */
export const isSsrPageConfig = isSsrPage;
